import React from 'react';
import { Container } from 'react-bootstrap';
import { Control, FieldErrors, UseFormRegister, useFieldArray, useForm } from 'react-hook-form';
import KaAlert from '../Common/KaAlert';
import useDisplayMessage from '../Common/useDisplayMessage';
import SpinnerButton from '../Common/SpinnerButton';
import { CenteredSpinner } from '../Common/CenteredSpinner';
import BoldButton from '../Common/BoldButton';
import useGetFieldAlyticsItems, { FieldAlyticsItem } from '../../requests/fieldalytics/useGetFieldAlyticsItems';
import useUpdateFieldAlyticsItems from '../../requests/fieldalytics/useUpdateFieldAlyticsItems';
import { useUserOrKaEmployee } from '../UserAccess/useUserOrKaEmployee';
import { LoginType } from '../UserAccess/UserContext';

interface FormItem {
  id: string;
  name: string;
  fieldAlytics: { id: string }[];
}

interface ProductMappingForm {
  items: FormItem[];
}

interface ItemRowProps {
  pIndex: number;
  control: Control<ProductMappingForm>;
  register: UseFormRegister<ProductMappingForm>;
  errors: FieldErrors<ProductMappingForm>;
}

const ItemRow = ({ pIndex, control, register, errors }: ItemRowProps) => {
  const { fields, remove, append } = useFieldArray({
    control,
    name: `items.${pIndex}.fieldAlytics`
  });

  return <>
    {fields.map((field, index) => {
      const err = errors.items?.[pIndex]?.fieldAlytics?.[index]?.id;
      return <div key={field.id} className="d-flex">
        <div className="w-100 py-1">
          <input
            type="text"
            key={field.id}
            data-testid={`items-${pIndex}-${index}-id`}
            {...register(`items.${pIndex}.fieldAlytics.${index}.id`, {
              required: (index > 0 && fields.length > 1) && 'Enter an ID or remove this entry'
            })}
            className={`border-dark form-control${err ? ' is-invalid' : ''}`} />
        </div>
        {fields.length > 1 &&
          <BoldButton
            className='btn-close btn-sm mt-2 ms-2'
            data-testid={`items-${pIndex}-${index}-remove`}
            onClick={() => remove(index)} />}
        <span className="invalid-feedback">{err?.message}</span>
      </div>;
    })}
    <div className="text-end pt-1">
      <BoldButton
        className='col-auto btn-ghost-primary'
        data-testid={`items-${pIndex}-add`}
        onClick={() => append({ id: '' })}>
        Add Mapping
      </BoldButton>
    </div>
  </>;
};

const ToFormItem = (item: FieldAlyticsItem): FormItem => {
  const productIds = item.productIds.length === 0 ? [''] : [...item.productIds];
  return {
    ...item,
    fieldAlytics: productIds.map(id => ({ id }))
  };
};

interface ProductMappingTableProps {
  items: FieldAlyticsItem[];
  displayMessageSuccess: (message: string) => void;
  displayMessageFail: (message: string) => void;
  disableSave?: boolean;
}

const ProductMappingTable = ({
  items,
  displayMessageSuccess,
  displayMessageFail,
  disableSave = false,
}: ProductMappingTableProps) => {
  const { register, handleSubmit, control, formState: { errors } } = useForm<ProductMappingForm>({
    defaultValues: { items: items.map(ToFormItem) }
  });

  const updateItems = useUpdateFieldAlyticsItems({
    onSuccess: () => displayMessageSuccess('Saved successfully'),
    onError: () => displayMessageFail('Unknown error, please contact Kahler Automation'),
  });

  const submit = (form: ProductMappingForm) => {
    form.items.forEach(p => {
      if (p.fieldAlytics.length === 1 && !p.fieldAlytics[0].id) {
        p.fieldAlytics.pop();
      }
    });

    updateItems.request(form.items.map(p => ({
      id: p.id,
      productIds: p.fieldAlytics.map(fa => fa.id),
    })));
  };

  return <>
    <table className="table table-hover table-bordered">
      <thead>
        <tr>
          <th className="w-50" scope="col">Constellation Product Name</th>
          <th className="w-50" scope="col">FieldAlytics Product Name</th>
        </tr>
      </thead>
      <tbody>
        {items.map((p, index) =>
          <tr key={p.id}>
            <th scope="row">
              <label>{p.name}</label>
            </th>
            <td>
              <ItemRow pIndex={index} register={register} control={control} errors={errors} />
            </td>
          </tr>)}
      </tbody>
    </table>
    <div className="w-100 mt-4">
      <SpinnerButton
        className="float-end w-25 btn-ghost-primary"
        onClick={handleSubmit(submit)}
        spinning={updateItems.isLoading}
        disabled={disableSave}>
        Save
      </SpinnerButton>
    </div>
  </>;
};

interface ProductMappingViewOnlyTableProps {
  items: FieldAlyticsItem[];
}

const ProductMappingViewOnlyTable = ({ items }: ProductMappingViewOnlyTableProps) =>
  <table className="table table-hover table-bordered">
    <thead>
      <tr>
        <th className="w-50">Constellation Product Name</th>
        <th className="w-50">FieldAlytics Product IDs</th>
      </tr>
    </thead>
    <tbody>
      {items.map(i => (
        <tr key={i.id}>
          <td>{i.name}</td>
          <td>{i.productIds.join(', ')}</td>
        </tr>
      ))}
    </tbody>
  </table>;

export const FieldAlyticsProductMappingPage = () => {
  const { userType } = useUserOrKaEmployee();
  const canEdit = userType === LoginType.User;
  const displayMessage = useDisplayMessage();
  const getItems = useGetFieldAlyticsItems({
    onError: (e) => displayMessage.fail(e.message),
  });

  return <Container>
    <h2 className="mb-5 ka-blue">FieldAlytics - Product Mapping</h2>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    {getItems.isLoading
      ? <CenteredSpinner />
      : canEdit
        ? <ProductMappingTable
          key={getItems.data ? 'loaded' : 'empty'}
          items={getItems.data ?? []}
          displayMessageSuccess={displayMessage.success}
          displayMessageFail={displayMessage.fail}
          disableSave={!getItems.data}
        />
        : <ProductMappingViewOnlyTable items={getItems.data ?? []} />}
  </Container>;
};
